import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// N-20 / REQ-6132 (#596): the Breakdown backend already bounds a subtree's
// BREADTH (`GET /items/{id}/breakdown`'s `limit`, comments_api.py —
// BREAKDOWN_DEFAULT_NODES=500 / BREAKDOWN_MAX_NODES=2000) rather than
// streaming an unbounded subtree — but a wide container within that bound
// still means mounting up to 2,000 ItemBlock rows (each carrying its own
// approve pill / comment thread / attribute panel / review-assignment state)
// in one paint, which is enough React+DOM work to visibly freeze the tab on
// open. A payload that is bounded-but-still-huge does not by itself satisfy
// "renders it without freezing" (#596 acceptance criteria) — this caps how
// many SIBLING rows render at once, at EVERY level of the tree (both the
// top-level list in BreakdownTab and each container's own children in
// ItemBlock use this), and reveals the rest a page at a time on demand
// instead of all together.
export const CHILDREN_PAGE_SIZE = 50;

/**
 * Renders the first `pageSize` of `items` via `renderItem`, plus a "Show N
 * more" affordance for the rest — a lightweight windowing alternative to a
 * measured/virtualized list (acceptable per #596: "virtualize ... or collapse
 * rows beyond N"). Chosen over `@tanstack/react-virtual` (already a dependency,
 * used by RequirementsTreeGrid.js) because the Breakdown's rows have GENUINELY
 * variable height (expandable comment threads, optional attributes, an
 * optionally-open review-assignment panel, recursive children) — a virtualizer
 * needs either a fixed row height or a `measureElement` pass, and getting that
 * wrong here would silently mis-scroll/clip a row's expanded content, which is
 * a worse failure mode than a few extra unmounted-until-clicked rows.
 *
 * `resetKey` remounts (and so re-collapses) the list when the identity of what
 * it's showing changes — e.g. the active item or filter — mirroring the reset
 * BreakdownTab already does for `expandedThreads`/`filter` on item switch.
 */
export default function ItemChildrenList({
  items, renderItem, pageSize = CHILDREN_PAGE_SIZE, resetKey = null, testIdSuffix = 'root',
}) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  useEffect(() => { setVisibleCount(pageSize); }, [resetKey, pageSize]);

  if (!items || items.length === 0) return null;
  const visible = items.slice(0, visibleCount);
  const hiddenCount = items.length - visible.length;

  return (
    <>
      {visible.map(renderItem)}
      {hiddenCount > 0 && (
        <button
          type="button"
          className="linkbtn"
          data-testid={`breakdown-show-more-${testIdSuffix}`}
          onClick={() => setVisibleCount((v) => v + pageSize)}
        >
          {`Show ${Math.min(hiddenCount, pageSize)} more (${hiddenCount} not shown)`}
        </button>
      )}
    </>
  );
}

ItemChildrenList.propTypes = {
  items: PropTypes.array.isRequired,
  renderItem: PropTypes.func.isRequired,
  pageSize: PropTypes.number,
   
  resetKey: PropTypes.any,
  testIdSuffix: PropTypes.string,
};
